import AppAuth from '../AppAuth'
import * as Sentry from '@sentry/react'
import {
  constructCommandCenterMetricDataPayloadFromResponses,
  constructExitSurveyDashboardPayload,
  constructFeedbackDetailsPayloadFromResponse,
  constructResidentFeedbackSummaryAndTimeSeriesPayload,
} from './metricDataUtilities'
import moment from 'moment'
import {mapFeedbackTypeToCategory} from '../../components/ResidentSentiment/DetailCards/CategoryRatingDetails'
import {capitalizeFirstLetter} from '../../Utils/StringFormatter'
import config from '../../amplifyconfiguration.json'

// TODO: this should be defined by an environment variable or something methinks
// check for prod, set accordingly
const isLocalhost = ['localhost', '127.0.0.1'].includes(window.location.hostname)
const domain = isLocalhost
  ? 'http://127.0.0.1:2345'
  : window.location.origin.includes('rxohome')
  ? 'https://operationanalytics.rxr.io'
  : 'https://operationanalytics-qa.rxr.io'

const TRANSCRIPT_PREVIEW_LIMIT = 180

const compactText = value => (value ?? '').toString().replace(/\s+/g, ' ').trim()

const truncateText = (value, limit = TRANSCRIPT_PREVIEW_LIMIT) => {
  const text = compactText(value)
  if (!text) {
    return ''
  }
  return text.length > limit ? `${text.slice(0, limit).trim()}...` : text
}

const getSentimentScore = conversation => {
  const probability = Number(conversation?.average_sentiment_score_probability)
  if (Number.isFinite(probability)) {
    return probability
  }
  const label = Number(conversation?.average_sentiment_score_label)
  return Number.isFinite(label) ? label : null
}

const getPreviewMessage = transcriptMessages => {
  if (!Array.isArray(transcriptMessages) || transcriptMessages.length === 0) {
    return null
  }
  return transcriptMessages.find(message => message?.resident_or_staff === 'resident') || transcriptMessages[0]
}

const buildConversationItem = conversation => {
  const score = getSentimentScore(conversation)
  if (!Number.isFinite(score)) {
    return null
  }

  const transcriptMessages = Array.isArray(conversation?.transcript_complex) ? conversation.transcript_complex : []
  const previewMessage = getPreviewMessage(transcriptMessages)
  const body = truncateText(previewMessage?.content || '')
  const transcriptText = typeof conversation?.transcript_text === 'string' ? conversation.transcript_text : ''

  return {
    id: conversation?.id,
    title: previewMessage?.sender_name || 'Conversation',
    subtitle: conversation?.building || '',
    date: conversation?.first_message_datetime || conversation?.last_message_datetime || '',
    body,
    metric: score.toFixed(2),
    score,
    transcriptMessages,
    transcriptText,
  }
}

class MetricDataAPI {
  /**
   * @param {string} buildingYardiId
   * @return {Promise<ResidentFeedbackSummaryAndTimeSeriesPayload>}
   */
  static async fetchResidentFeedbackSummaryAndTimeSeriesData(buildingYardiId) {
    let fetchResult
    let resp

    try {
      fetchResult = await MetricDataAPI.fetch('GET', '/app/satisfaction', {
        building: buildingYardiId,
        asOfDate: moment().format('YYYY-MM-DD'),
      })
      resp = fetchResult
    } catch (err) {
      console.error(err)
      resp = {}
    }

    return constructResidentFeedbackSummaryAndTimeSeriesPayload(resp)
  }

  /**
   * @param {string} buildingYardiId
   * @param {Date|string} asOfDate
   * @return {Promise<FeedbackDetailsPayload>}
   */
  static async fetchNPSScoreDetails(buildingYardiId, asOfDate) {
    let fetchResult
    let resp

    try {
      fetchResult = await MetricDataAPI.fetch('GET', '/app/nps_details', {
        building: buildingYardiId,
        asOfDate: moment(asOfDate).format('YYYY-MM-DD'),
      })
    } catch (err) {
      console.error(err)
      resp = {
        total: 'N/A',
        average: 'N/A',
        items: [],
      }
    }

    resp = {
      total: fetchResult?.agg?.count || 'N/A',
      average: fetchResult?.agg?.rating || 'N/A',
      items: (fetchResult?.detail || []).map(rating => {
        return {
          label: `${rating?.displayName}, Unit #${rating?.unit_number}`,
          value: rating?.rating,
          createdAt: moment(rating?.dt).format('YYYY-MM-DD'),
          description: rating?.feedback,
          denominator: 10,
        }
      }),
    }

    return constructFeedbackDetailsPayloadFromResponse(resp)
  }

  /**
   * @param {string} buildingYardiId
   * @param {Date|string} asOfDate
   * @return {Promise<FeedbackDetailsPayload>}
   */
  static async fetchSentimentDetails(buildingYardiId, asOfDate) {
    let fetchResult
    let resp

    try {
      fetchResult = await MetricDataAPI.fetch('GET', '/app/sentiment_transcripts', {
        building: buildingYardiId,
        asOfDate: moment(asOfDate).format('YYYY-MM-DD'),
      })
    } catch (err) {
      console.error(err)
      resp = {
        total: 'N/A',
        average: 'N/A',
        bottom: [],
        top: [],
      }
    }

    const conversations = Array.isArray(fetchResult?.conversations) ? fetchResult.conversations : []
    const items = conversations.map(buildConversationItem).filter(Boolean)
    const scores = items.map(item => item.score)

    const total = Number.isFinite(fetchResult?.agg?.count) ? fetchResult.agg.count : items.length
    const average = scores.length ? scores.reduce((sum, score) => sum + score, 0) / scores.length : 'N/A'

    const sorted = [...items].sort((a, b) => b.score - a.score)
    const top = sorted.filter(item => item.score >= 0).slice(0, 10)
    const bottom = [...sorted]
      .reverse()
      .filter(item => item.score < 0)
      .slice(0, 10)

    resp = {
      total,
      average,
      top,
      bottom,
    }

    return resp
  }

  /**
   * @param {string} buildingYardiId
   * @param {Date|string} asOfDate
   * @return {Promise<FeedbackDetailsPayload>}
   */
  static async fetchFeedbackCategoryDetails(buildingYardiId, asOfDate, types = []) {
    let fetchResult
    let resp

    try {
      fetchResult = await MetricDataAPI.fetch('GET', '/app/service_maintenance_amenity_rating_details', {
        building: buildingYardiId,
        asOfDate: moment(asOfDate).format('YYYY-MM-DD'),
        types,
      })
    } catch (err) {
      console.error(err)
      resp = {
        total: 'N/A',
        average: 'N/A',
        items: [],
      }
    }

    resp = {
      total: fetchResult?.agg?.count || 'N/A',
      average: fetchResult?.agg?.rating || 'N/A',
      items: (fetchResult?.detail || []).map(rating => {
        return {
          label: `${rating?.displayName}, Unit #${rating?.unit_number}`,
          secondaryLabel: capitalizeFirstLetter(mapFeedbackTypeToCategory[rating?.type]),
          value: rating?.rating,
          createdAt: moment(rating?.dt).format('YYYY-MM-DD'),
          description: rating?.feedback,
          denominator: 5,
          type: rating?.type,
        }
      }),
    }

    return constructFeedbackDetailsPayloadFromResponse(resp)
  }

  /**
   * @param {string} buildingId
   * @param {Date} asOfDate
   * @returns {Promise<CommandCenterMetricDataPayload>}
   */
  static async fetchCommandCenterData(buildingId, asOfDate) {
    const resp = await MetricDataAPI.fetch('GET', '/app/rxohome/commandcenter', {
      building: buildingId,
      start_date: moment(asOfDate).format('YYYY-MM-DD'),
      end_date: moment().format('YYYY-MM-DD'), // Yoann says use the same date for now?
    })

    return constructCommandCenterMetricDataPayloadFromResponses(resp)
  }

  // -------------------------------------------------------------------------------------------------

  /**
   * @param {string} buildingYardiId
   * @returns {Promise<ExitSurveyDashboardPayload>}
   */
  static async getExitSurveyDashboard(buildingYardiId) {
    const statsResponse = await MetricDataAPI.fetch('GET', '/app/exit_surveys', {
      building: buildingYardiId,
      asOfDate: moment().format('YYYY-MM-DD'),
    })

    const submissionsResponse = await MetricDataAPI.fetch('GET', '/app/exit_surveys/submissions', {
      building: buildingYardiId,
      asOfDate: moment().format('YYYY-MM-DD'),
    })

    return constructExitSurveyDashboardPayload(statsResponse, submissionsResponse)
  }

  // -------------------------------------------------------------------------------------------------

  /**
   * @param {*} params
   * @return {string}
   */
  static getQueryStringFromParams(params) {
    return !params
      ? ''
      : Object.entries(params)
          .map(tuple => {
            if (Array.isArray(tuple[1])) {
              return tuple[1].map(value => encodeURI(`${tuple[0]}=${value}`), '').join('&')
            }
            return encodeURI(`${tuple[0]}=${tuple[1]}`)
          })
          .join('&')
  }

  /**
   * @return {Promise<string>}
   */
  static async getJWT() {
    let jwt = await AppAuth.Instance().getJWT()
    if (!jwt) {
      throw new Error('Not authenticated')
    }

    return jwt
  }

  /**
   * @param {string} method
   * @param {string} path
   * @param {*?} params
   * @return {Promise<*>}
   */
  static async fetch(method, path, params) {
    if (!path) {
      throw new Error('Missing path')
    }

    const token = await MetricDataAPI.getJWT()

    if (method === 'GET') {
      // we pass params as the query string for Get requests
      const queryParams = MetricDataAPI.getQueryStringFromParams(params)
      // move the params into our path
      path = `${path}?${queryParams}`
      // delete the params
      params = undefined
    }

    // attach a leading slash in case the path doesn't have it
    const res = await fetch(`${domain}${path[0] === '/' ? '' : '/'}${path}`, {
      method: method,
      headers: {
        Authorization: 'Bearer ' + token,
        Region: config.aws_cognito_region,
        Pool: config.aws_user_pools_id,
        App: config.aws_user_pools_web_client_id,
      },
      body: params,
    })
    // check the status:
    //if it's not ok, return why it's not ok
    //if it's ok, return the data
    const status = res.status
    if (status !== 200) {
      // Log to Sentry for all non-200 status codes
      Sentry.captureException(new Error(`API request failed with status ${status}`), {
        extra: {
          method,
          path,
          status,
          params,
          domain,
        },
      })

      if (status === 401) {
        throw new UnauthorizedError()
      }
      if (status === 400) {
        throw new BadRequestError()
      }
      if (status === 404) {
        throw new NotFoundError()
      }
      if (status === 408) {
        throw new RequestTimeoutError()
      }
      if (status === 500) {
        throw new InternalServerError()
      }
      throw new Error(res)
    }
    return await res.json()
  }
}

class UnauthorizedError extends Error {
  constructor() {
    super('Not authorized')
  }
}

class BadRequestError extends Error {
  constructor() {
    super('Malformed request')
  }
}

class NotFoundError extends Error {
  constructor() {
    super('Cannot find the requested resource')
  }
}

class RequestTimeoutError extends Error {
  constructor() {
    super('Request timed out')
  }
}

class InternalServerError extends Error {
  constructor() {
    super('Internal server error')
  }
}

export {UnauthorizedError, BadRequestError, NotFoundError, RequestTimeoutError, InternalServerError}
export default MetricDataAPI
